import api from '../baseAPIs/core';
import { CustomilyTemplate, CustomilyTemplateDto } from '@extTypes/api/customily/template';
import { LibraryCategory } from '@extTypes/api/customily/libraries';
import type {
    LinkedPodProductsResponse,
    ManualTemplateProductList,
    PrintAreaCombinationDTO,
    ProviderTemplateProductDTO,
    ResultListDTO
} from './typings';
import { TemplateProductVariants } from '@extTypes/providers/manualPod/product';
import { BestMatchPodManualDTO, PodPlaceholders, TemplatePrintAreasDTO } from '@extTypes/pod/productInfo';

export const getProductTemplate = async (productId: string, useListEPS = false): Promise<CustomilyTemplate> => {
    const response = await api.get('Product', {
        params: {
            productId,
            useListEPS
        }
    });

    return response.data;
};

export const getTemplates = async (
    searchString: string,
    from: number,
    count: number
): Promise<CustomilyTemplateDto[]> => {
    const response = await api.get('Product/GetProductsFromTo', {
        params: {
            searchString: searchString,
            from: from,
            to: count
        }
    });

    return response.data;
};

export const getTemplatesCount = async (searchString: string): Promise<number> => {
    const response = await api.get('Product/GetProductsCount', {
        params: {
            searchString: searchString
        }
    });

    return response.data;
};

export const deleteTemplateById = (templateId: string, forceDelete?: boolean): Promise<void> => {
    return api.delete('Product/DeleteProductById', {
        params: {
            id: templateId,
            forceDelete
        }
    });
};

export const getProductLibraryName = async (id: number): Promise<string> => {
    const response = await api.get(`Libraries/${id}/Name`);

    return response.data;
};

export const getProductLibraryCategories = async (id: number): Promise<LibraryCategory[]> => {
    const response = await api.get(`Libraries/${id}/Categories`, {
        params: {
            searchString: '',
            from: 0,
            count: 0
        }
    });

    return response.data;
};

export const getLinkedPodProducts = async (
    podService: 'PRINTFUL' | 'PRINTIFY',
    podProductId?: string
): Promise<LinkedPodProductsResponse> => {
    const response = await api.get('TemplateProducts/LinkedPodProducts', {
        params: { podService, podProductId }
    });
    return response.data;
};

export const getLinkedTemplateByProducts = async (
    podService: 'PRINTFUL' | 'PRINTIFY',
    productId: string
): Promise<{
    podData: { [templateId: string]: string[] } | { [printProviderId: string]: Map<string, string[]> };
    printProviderData: { printProvider: string; isMultiFile: boolean }[];
}> => {
    const response = await api.get('TemplateProducts/LinkedPodVariants', {
        params: { productId, podService }
    });

    return response.data;
};

export const getManualPodProducts = async (podId: number): Promise<ManualTemplateProductList> => {
    return api
        .get<ManualTemplateProductList>('TemplateProducts/ManualPodProducts', {
            params: {
                podId
            }
        })
        .then(({ data }) => data);
};

export const getTemplateProductById = async (templateId: string): Promise<ProviderTemplateProductDTO> => {
    return api
        .get<ProviderTemplateProductDTO>(`TemplateProducts/GetTemplateProduct/${templateId}`)
        .then(({ data }) => data);
};

export const getTemplateProductVariantsByProductId = (id: string): Promise<TemplateProductVariants> =>
    api.get(`TemplateProductVariants/${id}`).then(({ data }) => data);

export const listTemplateProducts = async (
    from: number,
    count: number,
    search?: string
): Promise<ResultListDTO<ProviderTemplateProductDTO>> =>
    api
        .get<ResultListDTO<ProviderTemplateProductDTO>>('/TemplateProducts/ListTemplateProducts', {
            params: { from, count, search }
        })
        .then(({ data }) => data);

export const getPrintAreasCombinations = async (
    podId: number,
    podProductId: string
): Promise<PrintAreaCombinationDTO[]> =>
    api
        .get<PrintAreaCombinationDTO[]>('/TemplateProducts/GetPrintAreaCombinations', {
            params: { podId, podProductId }
        })
        .then(({ data }) => data);

export const getBestMatchTemplateProductIds = async (podPlaceholders: PodPlaceholders): Promise<string[]> => {
    const response = await api.post('Product/BestMatchTemplateProductIds', podPlaceholders);
    return response.data;
};

export const getBestMatchPodManual = async (
    bestMatchPodManualDTO: BestMatchPodManualDTO
): Promise<TemplatePrintAreasDTO> => {
    const response = await api.post('TemplateProducts/BestMatchPodManual', bestMatchPodManualDTO);
    return response.data;
};
